import { useLocation } from 'wouter';

import { useActiveProjectResolver } from '@/hooks/v2/useActiveProjectResolver';
import { useProjects } from '@/hooks/v2/useProjects';
import { useShortIdFromLocation } from '@/hooks/v2/useShortIdFromLocation';

/**
 * Resolves the project `short_id` that should drive `useProjectByShortId` +
 * `useConversation` for the current surface.
 *
 * - URL wins (path segments + `/?project=` via {@link useShortIdFromLocation})
 *   ONLY when it matches a project the user can still see — a stale
 *   `?project=ana0002` left over from an archived/deleted project must not
 *   keep firing 404s against `/api/v1/projects/by-short-id/...`.
 * - Matching treats **either** `short_id` **or** UUID `id` as the same project
 *   and always forwards **`short_id`** to `useProjectByShortId` so dashboard
 *   tabs stay keyed consistently even if a legacy URL used UUID for non-overview routes.
 * - On bare `/`, uses {@link useActiveProjectResolver}: multi-project lobby
 *   yields `null`; single-project still resolves the lone `short_id` until the
 *   page redirects to overview.
 */
export function useConversationSurfaceShortId(): string | null {
  const urlShortId = useShortIdFromLocation();
  const [path] = useLocation();
  const resolver = useActiveProjectResolver();
  const projects = useProjects();

  if (urlShortId !== null) {
    // While the list is still loading we trust the URL — pessimistic gating
    // would block path-based project routes on every cold load.
    if (projects.isLoading || !projects.data) return urlShortId;
    const projectHit = projects.data.find(
      (p) => p.short_id === urlShortId || p.id === urlShortId,
    );
    if (projectHit) return projectHit.short_id;
    // URL shortId doesn't match any visible project (archived/deleted/etc).
    // On home we fall back to the resolver (most-recent / single project).
    // On project routes we return null so the project page can render its
    // own not-found state instead of hammering the API.
    if (path === '/' || path === '') {
      if (resolver.phase === 'ready' && resolver.shortId) return resolver.shortId;
    }
    return null;
  }

  if (path === '/' || path === '') {
    if (resolver.phase === 'ready' && resolver.shortId) return resolver.shortId;
    return null;
  }

  return null;
}
